/**
 * theme.js — the design token object `T`, the single source of truth for
 * KandiKonnect's visual system. Every page and component imports from here;
 * never redeclare a local `T`.
 *
 * The register (see /PRODUCT.md): dark venue, neon signal. Near-black
 * surfaces are the room; the saturated accents are the lights and always
 * signal something (identity, action, live-ness).
 */

export const T = {
  // ── Surfaces ────────────────────────────────────────────────────────────
  // Cards read as matte hardware panels (flight case / mixer chassis), not
  // floating tinted glass — solid enough that backdrop-blur barely matters.
  // True glass is reserved for actual overlays (modalCard, drawerBg).
  bg:          "linear-gradient(160deg, #0a0515 0%, #0a0a0f 55%, #050a14 100%)",
  card:        "rgba(21,15,34,0.6)",       // matte panel on the page bg
  cardSoft:    "rgba(26,19,42,0.72)",      // slightly lifted panel (dropdowns)
  modalCard:   "rgba(16,8,32,0.96)",       // opaque card for lightboxes/modals
  drawerBg:    "rgba(8,4,18,0.98)",        // admin drawer / full-height panels

  // ── Borders & radii ─────────────────────────────────────────────────────
  border:      "1px solid rgba(255,255,255,0.1)",
  borderSoft:  "1px solid rgba(255,255,255,0.11)",
  borderHover: "1px solid rgba(180,79,255,0.4)",
  radius:      20,                          // cards
  radiusLg:    24,                          // page-level cards, modals

  // ── Text ────────────────────────────────────────────────────────────────
  textPrimary: "#f0f0ff",
  textMuted:   "rgba(255,255,255,0.45)",   // labels/captions only — below AA for body copy
  textStrong:  "rgba(255,255,255,0.85)",   // non-linked usernames, emphasized body

  // ── Neon accents ────────────────────────────────────────────────────────
  purple:      "#b44fff",
  pink:        "#ff3cac",
  cyan:        "#00f5ff",
  gold:        "#ffd700",
  green:       "#39ff14",
  red:         "#ff4444",
  photoPink:   "#d946ef",                  // photo_post activity accent

  // ── Inputs ──────────────────────────────────────────────────────────────
  inputBg:     "rgba(255,255,255,0.07)",
  inputBorder: "1px solid rgba(255,255,255,0.15)",

  // ── Legacy aliases (older files reference these names; prefer canonical) ─
  text:         "#f0f0ff",                  // → textPrimary
  muted:        "rgba(255,255,255,0.45)",  // → textMuted
  accentPurple: "#b44fff",                  // → purple
  accentPink:   "#ff3cac",                  // → pink
  accentCyan:   "#00f5ff",                  // → cyan
  accentGold:   "#ffd700",                  // → gold
  cardBg:       "rgba(21,15,34,0.6)",      // → card
  cardBorder:   "1px solid rgba(255,255,255,0.1)", // → borderSoft
  cardRadius:   24,                         // → radiusLg
  cardPad:      "36px 32px",                // page-card padding (scan/kandi pages)
};

export default T;
